import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'
import { Box, Tooltip } from '@mui/material'
import { useSelector } from 'react-redux'
import { useModalContext } from '../../../../ui/modal/contexts'
import { ThemeButton, ConfirmationDialog } from '../../../../ui'
import { useAppsDetail } from '../../hooks'
import { CancelAppDialog } from '../dialogs'
import { CANT_PASS_TO_GUILD_STATES, CANT_CANCEL_APPLICATION_STATES } from '../../constants'
import { CommunicationsBtn } from '../communications'
import { feedback } from '../../../../core/feedback'

export const HeaderRightBtns = ({ application, setApplication }) => {
  const { formatMessage } = useIntl()
  const { role, empresa_id, gremio_id } = useSelector((state) => state.global)
  const {
    actions: { open },
  } = useModalContext()
  const { getAppById, cancelApplication, appJoinGuild, acceptBudgetCall } = useAppsDetail()

  const canUpdateApplication =
    (role === 'gremio' && application.gremio_id) || (role !== 'gremio' && !application.gremio_id)

  const canAcceptBudget =
    application['estado'] === '30' &&
    (application?.empresa_ca?.empresa_id === empresa_id ||
      application?.empresa_ca?.gremio_id === parseInt(gremio_id)) &&
    application['camino_solicitud'] === '01' &&
    !application?.presupuesto_exterior?.precio_instalacion_tuberia_con_iva &&
    application?.tipo_solicitud === 'instalador'

  const notPermittedState = CANT_PASS_TO_GUILD_STATES[application.estado]

  const canPassApplicationToGuild =
    !application.gremio_id &&
    application?.empresa_ca?.gremio_id &&
    application?.empresa_ca?.aceptacion_gremio &&
    !notPermittedState
  const updateApplication = () => {
    getAppById(application.id).then(({ data }) => {
      setApplication(data)
    })
    //refresh bitacora if needed
  }

  const handleOpenCancelModal = () => {
    open({
      Component: CancelAppDialog,
      data: {
        estadoSolicitud: application['estado'],
        id: application['id'],
        cancelApplication,
        updateApplication,
      },
      type: 'centered',
    })
  }

  const assignAppToAssociation = () => {
    appJoinGuild(application.id, application.empresa_ca.gremio_id)
      .then(({ data }) => {
        setApplication(data)
      })
      .catch(() => console.debug('error in application joining guild'))
  }

  const handleAssignToAssociation = () => {
    open({
      Component: ConfirmationDialog,
      data: {
        title: formatMessage({ id: 'pages.application.view.documentRequestAssociation.title' }),
        text: formatMessage({ id: 'pages.application.view.documentRequestAssociation.text' }),
        yesText: formatMessage({ id: 'pages.application.view.documentRequestAssociation.yes' }),
        noText: formatMessage({ id: 'pages.application.view.documentRequestAssociation.no' }),
        yesAction: assignAppToAssociation,
      },
      type: 'centered',
    })
  }

  const acceptBudget = async () => {
    await acceptBudgetCall(application?.presupuesto_exterior?.id)
      .then(({ data }) => {
        setApplication(data)
      })
      .catch((error) => {
        const errorData = error?.response?.data
        const errorCode = errorData?.code
        const errorMessage = errorData?.message

        if (errorCode === 3512 && errorMessage) {
          feedback('error', errorMessage)
        } else {
          feedback(
            'error',
            formatMessage(
              {
                id: 'pages.application.view.acceptBudget.error',
              },
              { error: error?.message || '' }
            )
          )
        }
      })
  }

  const handleAcceptBudget = useCallback(
    () => {
      open({
        Component: ConfirmationDialog,
        data: {
          title: formatMessage({ id: 'pages.application.view.acceptBudget.title' }),
          text: formatMessage({ id: 'pages.application.view.acceptBudget.text' }),
          yesText: formatMessage({ id: 'pages.application.view.acceptBudget.yes' }),
          noText: formatMessage({ id: 'pages.application.view.acceptBudget.no' }),
          yesAction: acceptBudget,
        },
      })
    },
    [application] // eslint-disable-line react-hooks/exhaustive-deps
  )

  return (
    <Box display="flex" justifyContent="flex-end">
      <Box mr={2}>
        <CommunicationsBtn {...{ application }} />
      </Box>
      {canAcceptBudget && (
        <Box mr={2}>
          <ThemeButton color="secondary" size="small" onClick={handleAcceptBudget}>
            {formatMessage({
              id: 'pages.application.detail.acceptButton.label',
            })}
          </ThemeButton>
        </Box>
      )}
      {canUpdateApplication && !CANT_CANCEL_APPLICATION_STATES[application['estado']] && (
        <Tooltip title={"Puedes consultar el presupuesto en 'Ver documentos'."}>
          <ThemeButton color="default" size="small" onClick={handleOpenCancelModal}>
            {formatMessage({ id: 'pages.application.detail.actionButtons.cancelApp' })}
          </ThemeButton>
        </Tooltip>
      )}
      {canPassApplicationToGuild ? (
        <Box ml={2}>
          <ThemeButton color="primary" size="small" onClick={handleAssignToAssociation}>
            {formatMessage({ id: 'pages.application.detail.actionButtons.asociarSolicitud' })}
          </ThemeButton>
        </Box>
      ) : null}
    </Box>
  )
}
